import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import CustomCursor from "./components/CustomCursor";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import SkillsSection from "./components/SkillsSection";
import ProjectsSection from "./components/ProjectsSection";
import AppShowcaseSection from "./components/AppShowcaseSection";
import ExperienceSection from "./components/ExperienceSection";
import AchievementsSection from "./components/AchievementsSection";
import PersonalLife from "./components/PersonalLife";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import { unlockEgg } from "./lib/utils";
import { useSystemMode } from "./context/SystemModeContext";
import SecretGame from "./components/SecretGame";
import FlappyEgg from "./components/FlappyEgg";
import SnakeGame from "./components/SnakeGame";
import ArcadeMenu from "./components/ArcadeMenu";
import MusicPlayer from "./components/MusicPlayer";

// ── NEW: Secret menu (shake + hidden tap zone) ──────────────────────────────
import SecretMenuProvider from "./components/SecretMobileMenu";

class SafeMount extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex items-center justify-center min-h-screen text-center p-8">
          <div>
            <p className="text-destructive font-mono text-sm mb-4">
              Component crashed: {this.state.error.message}
            </p>
            <button
              onClick={() => { this.setState({ error: null }); this.props.onExit?.(); }}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--foreground))" }}
            >
              ← Back
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Portfolio = () => {
  const { isOverdrive, isDebug } = useSystemMode();
  const [mode, setMode] = React.useState("portfolio");
  const [musicActive, setMusicActive] = useState(false);

  // ── Handler for secret menu navigation ─────────────────────────────────────
  const handleSecretNavigate = (destination) => {
    if (destination === "game") {
      unlockEgg("game");
      setMode("game");
    } else if (destination === "music") {
      unlockEgg("music");
      setMusicActive(true);
    }
  };

  // Scroll easter egg
  useEffect(() => {
    const onScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 10) {
        const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
        if (!discovered.includes("scroll")) {
          unlockEgg("scroll");
          console.log("You found the bottom. Engineers always scroll.");
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Keyboard easter eggs (desktop: "game", "play", "unlock", "music")
  useEffect(() => {
    let buffer = "";
    let lastTriggerTime = 0;
    const MAGIC_WORDS = ["game", "play", "unlock"];
    const BUFFER_LIMIT = 20;
    const COOLDOWN_MS = 1500;

    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (isTyping || e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === "Escape") setMode("portfolio");
      if (e.key.length === 1) {
        buffer += e.key.toLowerCase();
        buffer = buffer.slice(-BUFFER_LIMIT);
      }

      const now = Date.now();
      for (let word of MAGIC_WORDS) {
        if (buffer.includes(word)) {
          if (now - lastTriggerTime < COOLDOWN_MS) return;
          lastTriggerTime = now;
          const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
          if (!discovered.includes("game")) unlockEgg("game");
          console.log("🎮 Secret Game Activated");
          setMode("game");
          buffer = "";
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setMode]);

  useEffect(() => {
    let buffer = "";
    const BUFFER_LIMIT = 20;

    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (isTyping || e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key.length === 1) {
        buffer += e.key.toLowerCase();
        buffer = buffer.slice(-BUFFER_LIMIT);
      }

      if (buffer.includes("music")) {
        buffer = "";
        setMusicActive(true);
        const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
        if (!discovered.includes("music")) {
          unlockEgg("music");
          console.log("🎵 Music mode unlocked. You type in style.");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const renderMode = () => {
    switch (mode) {
      case "portfolio":
        return (
          <div key="portfolio">
            <CustomCursor />
            {/*
              Navbar receives nothing extra — it reads handleSecretTap
              from SecretMenuContext internally.
            */}
            <Navbar />
            <main>
              <HeroSection />
              <AboutSection />
              <SkillsSection />
              <ProjectsSection />
              <AppShowcaseSection />
              <ExperienceSection />
              <AchievementsSection />
              <PersonalLife />
              <ContactSection />
            </main>
            <Footer />
          </div>
        );
      case "game":
        return (
          <SafeMount key="game" onExit={() => setMode("portfolio")}>
            <ArcadeMenu mode={mode} setMode={setMode} />
          </SafeMount>
        );
      case "flappy":
        return (
          <SafeMount key="flappy" onExit={() => setMode("game")}>
            <FlappyEgg onExit={() => setMode("game")} />
          </SafeMount>
        );
      case "secret":
        return (
          <SafeMount key="secret" onExit={() => setMode("game")}>
            <SecretGame onExit={() => setMode("game")} />
          </SafeMount>
        );
      case "snake":
        return (
          <SafeMount key="snake" onExit={() => setMode("game")}>
            <SnakeGame onExit={() => setMode("game")} />
          </SafeMount>
        );
      default:
        return null;
    }
  };

  return (
    /*
      SecretMenuProvider wraps the whole portfolio.
      - Listens for shake (mobile DeviceMotion)
      - Exposes handleSecretTap via context (Navbar picks it up)
      - Renders the drawer overlay
      - Calls handleSecretNavigate to switch mode / activate music
    */
    <SecretMenuProvider onNavigate={handleSecretNavigate}>
      <div className="min-h-screen bg-background text-foreground overflow-hidden">
        {renderMode()}

        <MusicPlayer
          active={musicActive}
          onClose={() => setMusicActive(false)}
        />

        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--foreground))",
            },
          }}
        />
      </div>
    </SecretMenuProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Portfolio />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;